import React from 'react';
import { Card, Table } from 'antd';
import type {
    TierDistribution,
    TierProgression,
    TierCoupons,
} from '../../../../stores/labatt-game';
import style from '../../style.module.scss';

interface TierDataTableProps {
    distribution: TierDistribution;
    progression?: TierProgression;
    coupons?: TierCoupons;
}

interface TierTableRow {
    key: number;
    tier: number;
    users: number;
    percentage: number;
    avgDays: string | null;
    coupons: number;
}

const getAverageDaysToTier = (
    tierNumber: number,
    progression?: TierProgression
): string | null => {
    if (tierNumber === 2 && progression?.avg_days_to_tier_2) {
        return progression.avg_days_to_tier_2;
    }
    if (tierNumber === 3 && progression?.avg_days_to_tier_3) {
        return progression.avg_days_to_tier_3;
    }
    return null;
};

const getCouponCountForTier = (
    tierNumber: number,
    coupons?: TierCoupons
): number => {
    if (!coupons) return 0;

    const couponCountMap: Record<number, keyof TierCoupons> = {
        1: 'tier_1_count',
        2: 'tier_2_count',
        3: 'tier_3_count',
    };

    return coupons[couponCountMap[tierNumber]] || 0;
};

const buildTierTableData = (
    distribution: TierDistribution,
    progression?: TierProgression,
    coupons?: TierCoupons
): TierTableRow[] => {
    return [1, 2, 3].map((tierNumber) => {
        const userCountKey = `tier_${tierNumber}_user_count` as keyof TierDistribution;
        const percentageKey = `tier_${tierNumber}_percentage` as keyof TierDistribution;

        const users = (distribution[userCountKey] as number) || 0;
        const percentage = (distribution[percentageKey] as number) || 0;

        const avgDays = getAverageDaysToTier(tierNumber, progression);
        const couponCount = getCouponCountForTier(tierNumber, coupons);

        return {
            key: tierNumber,
            tier: tierNumber,
            users,
            percentage,
            avgDays,
            coupons: couponCount,
        };
    });
};

const renderPercentageCell = (_: unknown, record: TierTableRow) =>
    record.percentage > 0 ? `${record.percentage.toFixed(0)}%` : '—';

const renderAverageDaysCell = (_: unknown, record: TierTableRow) =>
    record.avgDays ? parseFloat(record.avgDays).toFixed(2) : '—';

const columns = [
    {
        title: 'Tier',
        dataIndex: 'tier',
        key: 'tier',
    },
    {
        title: 'Users',
        key: 'users',
        dataIndex: 'users',
        align: 'right' as const,
    },
    {
        title: 'Percentage',
        key: 'percentage',
        render: renderPercentageCell,
        align: 'right' as const,
    },
    {
        title: 'Avg. Days to Tier',
        key: 'avgDays',
        render: renderAverageDaysCell,
        align: 'right' as const,
    },
    {
        title: 'Coupons',
        key: 'coupons',
        dataIndex: 'coupons',
    },
];

export const TierDataTable: React.FC<TierDataTableProps> = ({
    distribution,
    progression,
    coupons,
}) => {
    const tableData = buildTierTableData(distribution, progression, coupons);

    return (
        <div className={style.section_spacing}>
            <h2 className={style.section_title}>Tier Data</h2>
            <Card className={style.user_behavior_card}>
                <Table
                    dataSource={tableData}
                    pagination={false}
                    columns={columns}
                />
            </Card>
        </div>
    );
};
